import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

function KPI({ label, value, sub, accent }) {
  return (
    <div className="rounded-thin border border-surface-border dark:border-surface-dark-border p-3 bg-surface-panel dark:bg-surface-dark-panel">
      <div className="text-xs uppercase text-surface-muted tracking-wider">{label}</div>
      <div className="text-2xl font-semibold" style={{ color: accent || undefined }}>{value}</div>
      {sub && <div className="text-xs text-surface-muted mt-1">{sub}</div>}
    </div>
  );
}

export default function RecordingCoverage() {
  const [range, setRange, apiParams] = useRangeState('last_7');
  const [tab, setTab] = useState('overview');
  const [filters, setFilters] = useState({});
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'recording_coverage', merged],
    queryFn: () => reportsApi.recordingCoverage(merged),
    keepPreviousData: true,
  });

  const overall  = data?.overall || {};
  const perAgent = data?.per_agent || [];
  const orphans  = data?.orphans || [];

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['agent']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">{data?.title || 'Recording coverage'}</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="recording-coverage" params={merged} disabled={isLoading} />
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Voice recordings"    value={overall.voice_count ?? '—'} />
        <KPI label="Screen recordings"   value={overall.screen_count ?? '—'} />
        <KPI label="Voice+Screen pair rate"
             value={overall.pair_rate_pct != null ? `${overall.pair_rate_pct}%` : '—'}
             sub={`${overall.paired_count ?? 0} paired`}
             accent={overall.pair_rate_pct != null && overall.pair_rate_pct < 90 ? '#F5A623' : '#3BD162'} />
        <KPI label="Screen-only orphans"
             value={overall.screen_only_orphans ?? '—'}
             sub={overall.orphan_rate_pct != null ? `${overall.orphan_rate_pct}% of screen recs` : ''}
             accent={overall.screen_only_orphans > 0 ? '#F5A623' : undefined} />
      </div>

      <div className="flex gap-1 border-b border-surface-border dark:border-surface-dark-border mb-3">
        {['overview','per_agent','orphans'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={'px-3 py-1.5 text-sm border-b-2 -mb-px ' +
              (tab === t ? 'border-continuum-500 text-continuum-700 dark:text-continuum-200 font-medium'
                         : 'border-transparent hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')}>
            {t === 'overview' ? 'Overview' : t === 'per_agent' ? 'Per agent' : `Orphans (${orphans.length})`}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="text-sm text-surface-muted">
          <p className="mb-2">
            <strong>Pair rate</strong> = share of voice recordings that have a linked screen recording. Ideally 100% for
            agents where both are enabled. If it's low, either the agent hasn't granted screen share, or the rebind
            step is missing (see 24HRS entry on screen-recording lifecycle).
          </p>
          <p className="mb-2">
            <strong>Orphans</strong> = screen recordings with no matching voice recording. Usually indicates either
            (a) the agent started screen share but the call never actually connected, or (b) the rebind failed to
            correlate a placeholder call_id to the real Asterisk uniqueid.
          </p>
          <p>
            The per-agent tab lists everyone with a voice/screen toggle setting, so you can spot agents where the
            toggle is off but coverage is expected.
          </p>
        </div>
      )}

      {tab === 'per_agent' && (
        <ReportTable
          headers={['extension','agent_name','voice_enabled','screen_enabled','voice_count','screen_count','paired_count','pair_rate_pct']}
          rows={perAgent}
          columnLabels={{
            extension: 'Ext', agent_name: 'Agent',
            voice_enabled: 'Voice toggle', screen_enabled: 'Screen toggle',
            voice_count: 'Voice #', screen_count: 'Screen #',
            paired_count: 'Paired #', pair_rate_pct: 'Pair %',
          }}
          formatters={{
            voice_enabled: (v) => v ? '✓' : '—',
            screen_enabled: (v) => v ? '✓' : '—',
            pair_rate_pct: (v) => v == null ? '—' : `${v}%`,
          }}
        />
      )}

      {tab === 'orphans' && (
        <ReportTable
          headers={['id','call_id','agent_extension','cluster','started_at','duration_sec','status']}
          rows={orphans}
          columnLabels={{
            id: 'ID', call_id: 'Placeholder call_id',
            agent_extension: 'Ext', cluster: 'DC',
            started_at: 'Started', duration_sec: 'Duration (s)', status: 'Status',
          }}
        />
      )}
    </div>
  );
}
